import type { IAddServiceCmd } from "@/data/commands/room/AddServiceCmd";
import type { IUpdateServiceCmd } from "@/data/commands/room/UpdateServiceCmd";
import { Factory } from "@/data/dao/Factory";
import { RequestCache } from "@/data/dao/RequestCache";
import type { IDataSource } from "@/data/dao/service/BaseServiceDao";
import { RoomDao } from "@/data/dao/service/RoomDao";
import { ServiceAvailableFor, ServiceType } from "@/data/model/Enums";
import type { IService } from "@/data/model/Service";

import { BaseModal } from "@/shared/elements/BaseModal";
import { logError } from "@/shared/logging/errors";
import { style } from "@/shared/ui/Style";

import { i18n } from "@/portal/Trans";
import { ImagePicker } from "@/portal/formControls/ImagePicker";
import { MultiLanguageInput } from "@/portal/formControls/MultiLanguageInput";
import { Select } from "@/portal/formControls/Select";
import { DymamicFormBuilder } from "@/portal/forms/DymamicFormBuilder";
import { UiNotification } from "@/portal/ui/Notification";

export class ServiceModal extends BaseModal {
    private $form: JQuery;
    private $iconField: JQuery;
    private imagePicker: ImagePicker<IService>;
    private nameField: MultiLanguageInput;
    private typeSelect: Select<{ service: ServiceType }>;

    private $configSection: JQuery;
    private $configInner: JQuery;
    private configForm: DymamicFormBuilder;

    private $submitBtn: JQuery;

    private locationId: string;
    private roomId: string;
    private model: IService;
    private takenServices: Array<ServiceType>;
    private atOrder: number;
    private availableFor?: ServiceAvailableFor;

    public onSaved?: { (translation: IService) };
    public onRemoved?: { () };

    constructor(
        locationId: string,
        roomId: string,
        model?: IService,
        atOrder?: number,
        takenServices?: Array<ServiceType>,
        availableFor?: ServiceAvailableFor
    ) {
        super(model === undefined ? i18n("modal.service.addTitle") : i18n("modal.service.editTitle"), "modal-xl", true);

        this.locationId = locationId;
        this.roomId = roomId;
        this.model = model!;
        this.takenServices = takenServices!;
        this.atOrder = atOrder!;
        this.availableFor = availableFor;
        this.$element.addClass("service");

        this.$form = $('<form class="kt-form kt-form--label-right" />').appendTo(this.$body);
        const createSection = $('<div class="kt-section kt-section--first"><div class="kt-section__body" /></div>')
            .appendTo(this.$form)
            .find("> .kt-section__body");
        createSection.append(
            '<div class="row"><label class="col-xl-4"></label><div class="col-lg-9 col-xl-6"><h3 class="kt-section__title kt-section__title-sm">' +
                i18n("modal.service.service") +
                "</h3></div></div>"
        );

        this.$form.append(
            '<div class="kt-separator kt-separator--border-dashed kt-separator--portlet-fit kt-separator--space-lg"></div>'
        );

        this.$configSection = $(
            '<div class="kt-section kt-section--last dynamicform"><div class="kt-section__body" /></div>'
        )
            .appendTo(this.$form)
            .find("> .kt-section__body");
        this.$configSection.append(
            '<div class="row"><label class="col-xl-4"></label><div class="col-lg-9 col-xl-6"><h3 class="kt-section__title kt-section__title-sm">' +
                i18n("modal.service.settings") +
                "</h3></div></div>"
        );
        this.$configInner = $('<div class="inner" />').appendTo(this.$configSection);
        this.configForm = new DymamicFormBuilder(this.$configInner, "col-xl-4", "col-lg-9 col-xl-6");

        const typeGroup = $(
            '<div class="form-group row"><label class="col-xl-4 col-form-label">' +
                i18n("modal.service.service") +
                ' <span class="mandatory">*</span></label><div class="col-xl-6" /></div>'
        )
            .appendTo(createSection)
            .find("> .col-xl-6");
        this.typeSelect = new Select<{ service: ServiceType }>(
            undefined,
            this.servicesDataSource,
            (c) => i18n("enums.ServiceType." + ServiceType[c.service]),
            true,
            undefined,
            "service"
        ).renderTo(typeGroup);
        this.typeSelect.onChange = () => {
            const s = this.typeSelect.getModel();
            this.buildServiceForm(s === undefined ? undefined : s.service);
        };

        this.typeSelect.fetchData(() => {
            if (model !== undefined) {
                this.typeSelect.setValue(model.serviceType);
                this.typeSelect.setDisabled(true);
            }

            const s = this.typeSelect.getModel();
            this.buildServiceForm(s === undefined ? undefined : s.service);
        });

        const iconGroup = $(
            '<div class="form-group row"><label class="col-xl-4 col-form-label">' +
                i18n("modal.service.customIcon") +
                '</label><div class="col-xl-6" /></div>'
        )
            .appendTo(createSection)
            .find("> .col-xl-6");
        this.$iconField = $('<input type="text" class="form-control" placeholder="z.B. fal fa-sort" />').appendTo(
            iconGroup
        );

        const imageGroup = $(
            '<div class="form-group row"><label class="col-xl-4 col-form-label">' +
                i18n("modal.service.image") +
                '</label><div class="col-xl-6" /></div>'
        )
            .appendTo(createSection)
            .find("> .col-xl-6");
        this.imagePicker = new ImagePicker<IService>(
            Factory.getInstance<RoomDao>(RoomDao).serviceImageDataSource(this.roomId)
        ).renderTo(imageGroup);
        this.imagePicker.setValue(this.model);

        const nameGroup = $(
            '<div class="form-group row"><label class="col-xl-4 col-form-label">' +
                i18n("modal.service.customDesignation") +
                '</label><div class="col-xl-6" /></div>'
        )
            .appendTo(createSection)
            .find("> .col-xl-6");
        const nameInput = $('<input type="text" class="form-control" />').appendTo(nameGroup);
        this.nameField = MultiLanguageInput.fromInput(nameInput);

        if (this.model !== undefined) {
            this.$iconField.val(this.model.iconClass === undefined ? "" : this.model.iconClass);

            this.nameField.setValues(this.model.name);
        }

        if (this.model !== undefined) {
            const deleteBtn = $(
                '<button class="btn btn-clean btn-text-danger btn-small">' + i18n("general.delete") + "</button>"
            )
                .appendTo(this.$footer!)
                .click((e) => {
                    e.preventDefault();

                    deleteBtn
                        .addClass("kt-spinner kt-spinner--left kt-spinner--sm kt-spinner--primary disabled")
                        .prop("disabled", true);
                    Factory.getInstance<RoomDao>(RoomDao)
                        .removeService(this.roomId, this.model.id)
                        .then(() => {
                            this.close();
                            if (this.onRemoved !== undefined) this.onRemoved();
                        })
                        .catch((ex) => {
                            logError(ex);
                            deleteBtn
                                .removeClass("kt-spinner kt-spinner--left kt-spinner--sm kt-spinner--primary disabled")
                                .prop("disabled", false);
                            UiNotification.showException(i18n("modal.service.failedDelete"));
                        });
                });
        }

        $('<button type="button" class="btn btn-secondary">' + i18n("general.cancel") + "</button>")
            .appendTo(this.$footer!)
            .click((e) => {
                e.preventDefault();
                this.close();
            });

        this.$submitBtn = $('<button type="button" class="btn btn-success">' + i18n("general.save") + "</button>")
            .appendTo(this.$footer!)
            .click((e) => {
                e.preventDefault();
                this.$form.submit();
            });

        this.$form.submit((e) => {
            e.preventDefault();
            e.stopPropagation();

            this.onSubmit();
            return false;
        });
    }

    private buildServiceForm(serviceType?: ServiceType): Promise<void> {
        return new Promise((resolve) => {
            if (serviceType === undefined) {
                resolve();
                return;
            }

            if (!this.$configSection.is(":visible")) this.$configSection.show();

            this.$configInner.html('<div class="kt-spinner kt-spinner--md kt-spinner--success"></div>');
            return RequestCache.getSchemaForService(serviceType, this.locationId).then((a) => {
                this.configForm.buildBySchema(a);
                if (this.model !== undefined) this.configForm.setValues(this.model.config);

                if (a.length === 0) this.$configSection.hide();

                resolve();
            });
        });
    }

    private onSubmit() {
        let cmd: IAddServiceCmd | IUpdateServiceCmd;

        let hasError = false;
        this.$form.find(".is-invalid").removeClass("is-invalid");
        const cfg = this.configForm.validateAndGet();
        if (cfg.hasError) hasError = true;

        if (this.model === undefined) {
            cmd = {
                config: cfg.values,
                iconClass: this.$iconField.val().length === 0 ? undefined : this.$iconField.val(),
                serviceType: parseInt(this.typeSelect.getValue()!),
                name: this.nameField.getValues(),
                order: this.atOrder,
                availableFor: this.availableFor,
            };

            if ((cmd as any).serviceType === undefined || isNaN((cmd as any).serviceType)) {
                this.typeSelect.setError(true);
                hasError = true;
            }
        } else {
            cmd = {
                config: cfg.values,
                iconClass: this.$iconField.val().length === 0 ? undefined : this.$iconField.val(),
                name: this.nameField.getValues(),
            };
        }

        if (hasError) return;

        const dao = Factory.getInstance<RoomDao>(RoomDao);
        const promise =
            this.model === undefined
                ? dao.addService(this.roomId, cmd as any)
                : dao.updateService(this.roomId, this.model.id, cmd as any);

        this.$submitBtn.addClass(style.buttonLoaderClass).prop("disabled", true).find("> .la").remove();
        promise
            .then((m) => this.imagePicker.handle(m))
            .then((m) => {
                this.$submitBtn.removeClass(style.buttonLoaderClass).prop("disabled", false);
                if (this.$submitBtn.find("> .la").length === 0) this.$submitBtn.prepend('<i class="la la-save" />');

                this.close();
                if (this.onSaved !== undefined) this.onSaved(m);
            })
            .catch((ex) => {
                logError(ex);
                this.$submitBtn.removeClass(style.buttonLoaderClass).prop("disabled", false);
                if (this.$submitBtn.find("> .la").length === 0) this.$submitBtn.prepend('<i class="la la-save" />');

                UiNotification.showException(i18n("modal.service.failedEdit"));
            });
    }

    private servicesDataSource: IDataSource<{ service: ServiceType }> = {
        getData: () => {
            return new Promise((resolve) => {
                if (this.model !== undefined) {
                    resolve({
                        items: [{ service: this.model.serviceType }],
                        offset: 0,
                        limit: 1,
                        hasNext: false,
                        hasPrev: false,
                    });
                    return;
                }

                RequestCache.getAvailableServices(this.locationId).then((services) => {
                    const t = services
                        .filter((a) => {
                            return (
                                this.takenServices.find(
                                    (c) =>
                                        c !== ServiceType.Order &&
                                        c !== ServiceType.Website &&
                                        c !== ServiceType.Offer &&
                                        c === a
                                ) === undefined
                            );
                        })
                        .map((c) => {
                            return {
                                service: c,
                            };
                        });

                    resolve({ items: t, offset: 0, limit: t.length, hasNext: false, hasPrev: false });
                });
            });
        },
    };
}
